import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Popover,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'dayjs/locale/vi';
import 'dayjs/locale/en';

// Extend dayjs with quarter plugin
dayjs.extend(quarterOfYear);

/**
 * Reusable Date Range Picker Component
 * Single input with popover containing two calendars for from/to date selection
 *
 * @param {String} label - Label for the date range picker
 * @param {String} fromValue - Selected from date value (YYYY-MM-DD format)
 * @param {String} toValue - Selected to date value (YYYY-MM-DD format)
 * @param {Function} onFromChange - Change handler for from date
 * @param {Function} onToChange - Change handler for to date
 * @param {Boolean} disabled - Whether date picker is disabled
 * @param {Object} sx - Additional Material UI sx styles
 * @param {String} id - Element id
 * @param {String} name - Element name
 * @param {String} data-testid - Test attribute
 * @param {String} minDate - Minimum selectable date (YYYY-MM-DD format)
 * @param {String} maxDate - Maximum selectable date (YYYY-MM-DD format)
 */
function DateRangePicker({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  disabled = false,
  sx = {},
  id,
  name,
  'data-testid': dataTestId,
  minDate,
  maxDate,
}) {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);

  // Set dayjs locale based on i18n language
  const locale = i18n.language === 'vi' ? 'vi' : 'en';

  // Convert values to dayjs objects
  const dayjsFromValue = fromValue ? dayjs(fromValue) : null;
  const dayjsToValue = toValue ? dayjs(toValue) : null;
  const dayjsMinDate = minDate ? dayjs(minDate) : dayjs('2000-01-01');
  const dayjsMaxDate = maxDate ? dayjs(maxDate) : null;

  // Format display value
  const getDisplayValue = () => {
    if (!fromValue && !toValue) return '';
    const fromDisplay = fromValue ? dayjs(fromValue).format('DD/MM/YYYY') : '...';
    const toDisplay = toValue ? dayjs(toValue).format('DD/MM/YYYY') : '...';
    return `${fromDisplay} - ${toDisplay}`;
  };

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFromDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      const formattedDate = newValue.format('YYYY-MM-DD');
      onFromChange({
        target: {
          name: `${name}-from`,
          value: formattedDate,
        },
      });
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      const formattedDate = newValue.format('YYYY-MM-DD');
      onToChange({
        target: {
          name: `${name}-to`,
          value: formattedDate,
        },
      });
    }
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onFromChange({
      target: {
        name: `${name}-from`,
        value: '',
      },
    });
    onToChange({
      target: {
        name: `${name}-to`,
        value: '',
      },
    });
  };

  // Preset date range options
  const getPresetRanges = () => {
    const today = dayjs();
    return [
      {
        label: t('dateRangePicker.presets.today'),
        from: today,
        to: today,
      },
      {
        label: t('dateRangePicker.presets.yesterday'),
        from: today.subtract(1, 'day'),
        to: today.subtract(1, 'day'),
      },
      {
        label: t('dateRangePicker.presets.thisWeek'),
        from: today.startOf('week'),
        to: today.endOf('week'),
      },
      {
        label: t('dateRangePicker.presets.lastWeek'),
        from: today.subtract(1, 'week').startOf('week'),
        to: today.subtract(1, 'week').endOf('week'),
      },
      {
        label: t('dateRangePicker.presets.thisMonth'),
        from: today.startOf('month'),
        to: today.endOf('month'),
      },
      {
        label: t('dateRangePicker.presets.lastMonth'),
        from: today.subtract(1, 'month').startOf('month'),
        to: today.subtract(1, 'month').endOf('month'),
      },
      {
        label: t('dateRangePicker.presets.thisQuarter'),
        from: today.startOf('quarter'),
        to: today.endOf('quarter'),
      },
      {
        label: t('dateRangePicker.presets.lastQuarter'),
        from: today.subtract(1, 'quarter').startOf('quarter'),
        to: today.subtract(1, 'quarter').endOf('quarter'),
      },
      {
        label: t('dateRangePicker.presets.thisYear'),
        from: today.startOf('year'),
        to: today.endOf('year'),
      },
      {
        label: t('dateRangePicker.presets.lastYear'),
        from: today.subtract(1, 'year').startOf('year'),
        to: today.subtract(1, 'year').endOf('year'),
      },
    ];
  };

  const handlePresetSelect = (preset) => {
    onFromChange({
      target: {
        name: `${name}-from`,
        value: preset.from.format('YYYY-MM-DD'),
      },
    });
    onToChange({
      target: {
        name: `${name}-to`,
        value: preset.to.format('YYYY-MM-DD'),
      },
    });
  };

  const open = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <Box sx={{ width: '100%', ...sx }}>
        <TextField
          ref={inputRef}
          label={label}
          value={getDisplayValue()}
          onClick={handleClick}
          disabled={disabled}
          fullWidth
          size="small"
          variant="outlined"
          placeholder="dd/mm/yyyy - dd/mm/yyyy"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <DateRangeIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (fromValue || toValue) && !disabled && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  aria-label="clear date range"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            id,
            name,
            'data-testid': dataTestId,
            style: { cursor: disabled ? 'default' : 'pointer' },
          }}
          sx={{
            '& .MuiInputBase-root': {
              cursor: disabled ? 'default' : 'pointer',
            },
            '& .MuiInputBase-input': {
              cursor: disabled ? 'default' : 'pointer',
              '&::placeholder': {
                color: 'text.disabled',
                opacity: 0.6,
              },
            },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Preset Options Sidebar */}
            <Box
              sx={{
                minWidth: 160,
                borderRight: { md: '1px solid #e0e0e0' },
                borderBottom: { xs: '1px solid #e0e0e0', md: 'none' },
                py: 1,
              }}
            >
              <Box sx={{ px: 2, py: 1, fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                {t('dateRangePicker.presets.title')}
              </Box>
              <List dense disablePadding sx={{ maxHeight: { xs: 150, md: 320 }, overflow: 'auto' }}>
                {getPresetRanges().map((preset, index) => (
                  <ListItemButton
                    key={index}
                    onClick={() => handlePresetSelect(preset)}
                    sx={{
                      py: 0.75,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    data-testid={`${dataTestId}-preset-${index}`}
                  >
                    <ListItemText
                      primary={preset.label}
                      slotProps={{
                        primary: {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* Calendars Section */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, p: 1 }}>
              {/* From Date Calendar */}
              <Box sx={{ borderRight: { sm: '1px solid #e0e0e0' }, pr: { sm: 1 } }}>
                <Box sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {t('dateRangePicker.fromDate')}
                </Box>
                <DateCalendar
                  value={dayjsFromValue}
                  onChange={handleFromDateChange}
                  minDate={dayjsMinDate}
                  maxDate={dayjsToValue || dayjsMaxDate}
                  sx={{
                    '& .MuiPickersDay-root': {
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              {/* To Date Calendar */}
              <Box sx={{ pl: { sm: 1 } }}>
                <Box sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {t('dateRangePicker.toDate')}
                </Box>
                <DateCalendar
                  value={dayjsToValue}
                  onChange={handleToDateChange}
                  minDate={dayjsFromValue || dayjsMinDate}
                  {...(dayjsMaxDate && { maxDate: dayjsMaxDate })}
                  sx={{
                    '& .MuiPickersDay-root': {
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: '#fff',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1.5, borderTop: '1px solid #e0e0e0' }}>
            <Button
              size="small"
              onClick={handleClear}
              id={`${id}-clear`}
              data-testid={`${dataTestId}-clear`}
            >
              {t('dateRangePicker.clear')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleClose}
              id={`${id}-apply`}
              data-testid={`${dataTestId}-apply`}
            >
              {t('dateRangePicker.apply')}
            </Button>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}

DateRangePicker.propTypes = {
  label: PropTypes.string,
  fromValue: PropTypes.string,
  toValue: PropTypes.string,
  onFromChange: PropTypes.func.isRequired,
  onToChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  id: PropTypes.string,
  name: PropTypes.string,
  'data-testid': PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
};

export default DateRangePicker;
